import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { SetupProgress, SetupProgressUpdate, SetupStepId } from '../types/setup';

const QUERY_KEY = 'setup-progress';

/**
 * Fetch the current setup progress for the organization.
 * Creates a new progress record if one doesn't exist.
 */
export function useSetupProgress() {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: async () => {
      const response = await api.get<SetupProgress>('/api/setup/progress/');
      return response.data;
    },
  });
}

/**
 * Update setup progress - mark a step as complete or update current step.
 */
export function useUpdateSetupProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SetupProgressUpdate) => {
      const response = await api.patch<SetupProgress>('/api/setup/progress/', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([QUERY_KEY], data);
    },
  });
}

/**
 * Mark a specific step as completed.
 */
export function useCompleteStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (stepId: SetupStepId) => {
      const response = await api.patch<SetupProgress>('/api/setup/progress/', {
        step: stepId,
        completed: true,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([QUERY_KEY], data);
    },
  });
}

/**
 * Mark the wizard as complete.
 */
export function useCompleteWizard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post<SetupProgress>('/api/setup/progress/complete/', {});
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([QUERY_KEY], data);
    },
  });
}

/**
 * Dismiss the dashboard reminder permanently.
 */
export function useDismissSetupReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post<SetupProgress>('/api/setup/progress/dismiss/', {});
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([QUERY_KEY], data);
    },
  });
}

/**
 * Reset all wizard progress (for testing/development).
 */
export function useResetSetupProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post<SetupProgress>('/api/setup/progress/reset/', {});
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([QUERY_KEY], data);
    },
  });
}

/**
 * Helper hook to check if a step is completed.
 */
export function useIsStepCompleted(stepId: SetupStepId): boolean {
  const { data: progress } = useSetupProgress();
  if (!progress) return false;

  const field = `${stepId}_completed` as keyof SetupProgress;
  return progress[field] as boolean;
}

/**
 * Helper hook to check if the wizard should be shown.
 * Returns true if wizard is not completed and not dismissed.
 */
export function useShouldShowSetupWizard(): boolean {
  const { data: progress, isLoading } = useSetupProgress();

  if (isLoading || !progress) return false;
  return !progress.wizard_completed && !progress.wizard_dismissed;
}
